import { Component, ErrorInfo, ReactNode } from 'react'
import { useTranslate } from '../hooks/useTranslate'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error?: Error
}

const ErrorFallbackContent: React.FC<{ error?: Error; onRetry: () => void }> = ({ error, onRetry }) => {
    const { t } = useTranslate();
    return (
        <div className="min-h-[400px] flex items-center justify-center">
            <div className="text-center p-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('Something went wrong')}</h3>
                <p className="text-gray-600 mb-4 max-w-md">
                    {t('An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.')}
                </p>
                <button
                    onClick={onRetry}
                    className="btn-primary"
                >
                    {t('Try Again')}
                </button>
                {import.meta.env.DEV && error && (
                    <details className="mt-4 text-left">
                        <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                            Error Details (Development)
                        </summary>
                        <pre className="mt-2 p-4 bg-gray-100 rounded-lg text-xs text-gray-800 overflow-auto">
                            {error.stack}
                        </pre>
                    </details>
                )}
            </div>
        </div>
    );
};

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false
    }

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            return (
                <ErrorFallbackContent
                    error={this.state.error}
                    onRetry={() => this.setState({ hasError: false, error: undefined })}
                />
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary